import {
  array,
  bool,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from 'prop-types'
import { useState } from 'react'
import { useUserRole } from '@opallabs/api'
import { navigateToMoment } from '@opallabs/kuiper'
import { AnalyticsConsumer, AnalyticsLayer } from '@opallabs/quanta'
import { useWeekViewSettings } from '../WeekViewSettingsContext'
import { DefaultMomentCard } from './Default'
import { buildContextMenuOptions } from './helpers'
import { MomentContextMenu } from './MomentContextMenu'
import PrivateMomentCard from './Private'
import './styles.scss'
import withDraggable from './withDraggable'

export const getCardContext = (moment) => {
  if (moment.is_ghost) return 'Private Card'
  if (moment.isPlaceholder) return 'Proto Card'
  if (moment.postCount || (moment.posts && moment.posts.length))
    return 'Default Card'
  return 'Empty Card'
}
export const getCardToRender = (moment) => {
  if (moment.is_ghost) return PrivateMomentCard
  return DefaultMomentCard
}

const MomentCard = ({
  activeFilters,
  selectedFilters,
  contextMenuCallbacks,
  isMomentDragActive,
  isSelectable,
  isSelected,
  moment,
  onDragRestrictedContent,
  pivotName,
  story,
  theme = 'light',
}) => {
  const { data: userRoleData } = useUserRole()
  const { collapseContent } = useWeekViewSettings()

  const [isRenamePopoverOpen, setPopoverState] = useState(false)
  const [isMomentSelected, setMomentSelected] = useState(isSelected)
  const toggleRenamePopover = (isOpen) => {
    setPopoverState(isOpen)
  }

  const onClickMoment = ({ navigateTo, primaryBrand, track }) => {
    if (isRenamePopoverOpen) return
    if (isSelectable) setMomentSelected(!isMomentSelected)
    track('click', { id: moment.id, type: 'moment' })
    navigateToMoment({
      momentId: moment.id,
      navigateTo,
      storyId: story && story.id,
      primaryBrand,
    })
  }

  const contextMenuOptions = buildContextMenuOptions({
    callbacks: contextMenuCallbacks,
    isExpanded: !collapseContent,
    isInStories: !!story,
    moment,
    onContextMenuRename: toggleRenamePopover.bind(this, true),
    storyId: story && story.id,
  })

  const contextName = getCardContext(moment)
  const CardToRender = getCardToRender(moment)

  return (
    <AnalyticsLayer name={contextName}>
      <AnalyticsConsumer>
        {({ track }) => (
          <MomentContextMenu
            contextMenuCallbacks={contextMenuCallbacks}
            isExpanded={!collapseContent}
            isInStories={Boolean(story)}
            moment={moment}
            disabled={moment?.is_ghost}
            onContextMenuRename={() => toggleRenamePopover(true)}
            storyId={story?.id}
            track={track}
          >
            <CardToRender
              activeFilters={activeFilters}
              selectedFilters={selectedFilters}
              contextMenuOptions={contextMenuOptions}
              closeRenamePopover={toggleRenamePopover.bind(this, false)}
              isContentLoading={
                moment.posts && moment.posts.length !== moment.postCount
              }
              isLimitedUser={userRoleData?.isRestrictedUser ?? true}
              isMomentDragActive={isMomentDragActive}
              isRenamePopoverOpen={isRenamePopoverOpen}
              isSelectable={isSelectable}
              isSelected={isMomentSelected}
              moment={moment}
              onClickMoment={(navigateTo, primaryBrand) =>
                onClickMoment({ navigateTo, primaryBrand, track })
              }
              onDragRestrictedContent={onDragRestrictedContent}
              pivotName={pivotName}
              story={story}
              {...{
                theme,
              }}
            />
          </MomentContextMenu>
        )}
      </AnalyticsConsumer>
    </AnalyticsLayer>
  )
}

MomentCard.propTypes = {
  activeFilters: array,
  selectedFilters: object,
  contextMenuCallbacks: object,
  isMomentDragActive: bool,
  isSelectable: bool,
  isSelected: bool,
  moment: object,
  onDragRestrictedContent: func,
  pivotName: oneOfType([number, string]),
  story: object,
  theme: oneOf(['dark', 'light']),
}

export default withDraggable(MomentCard)
